import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {isMobile} from 'react-device-detect';
import type {AiGeneratedContent, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextPromptBox} from '@Components/ai-text-prompt-box';
import {noop} from '@Utils/general.util';
import {getRecentlyGeneratedTranscripts, reWriteTranscript, writeTranscript} from '@Libraries/text-to-speech.library';
import type {AiWriterViewProps} from './ai-writer-view.types';
import {RECENT_TRANSCRIPTS_PAGE_SIZE} from './ai-writer-view.types';
import styles from './ai-writer-view.module.scss';

export function AiWriterView({isOpenedInPopOver, isAiWriterDropdownOpen, onBack, onClose, onContentSelected}: AiWriterViewProps): ReactElement<HTMLDivElement> {
  const writingIdToReuse = useRef<string | undefined>(undefined);

  const handleContentSelected = (selectedContent: AiGeneratedContent): void => {
    onContentSelected(selectedContent.text);
  };

  const useContent = (): Promise<void> => {
    return Promise.resolve();
  };

  const reuseContent = (id: string): Promise<void> => {
    return new Promise((res) => {
      writingIdToReuse.current = id;
      res();
    });
  };

  const alterContent = (_prompt: string, selectedValues: SelectedValueType): Promise<AiGeneratedContent> => {
    if (writingIdToReuse.current) {
      return reWriteTranscript(writingIdToReuse.current, selectedValues);
    }

    throw new Error('Race condition hit. Generated writing id is undefined. reuseContent should have been called before alterContent.');
  };

  const titles = {
    main: window.i18next.t('pmwjs_ai_writer'),
    recents: window.i18next.t('pmwjs_recent_generations'),
    selected: window.i18next.t('pmwjs_selected_script'),
    primarySelectedContent: '',
  };

  const alterationLoadingText = {
    primaryAlterationLoadingMessage: window.i18next.t('pmwjs_updating'),
  };

  return (
    <div className={'_full-height'}>
      <AiTextPromptBox
        ref={undefined}
        isOpenedInPopOver={isOpenedInPopOver}
        showBackButton={!isAiWriterDropdownOpen}
        sectionsContainerClass={`_full-height ${styles.aiWriterNoGradientBorder}`}
        promptboxContainerClass={'_full-height'}
        contentClassName={`spacing-p-r-0 spacing-p-l-0 flex-v-row`}
        innerContentClassName={`spacing-p-r-3 spacing-p-l-3 _full-height`}
        isInMobileMode={isMobile}
        titles={titles}
        onContentSelected={handleContentSelected}
        getRecents={getRecentlyGeneratedTranscripts.bind(null, RECENT_TRANSCRIPTS_PAGE_SIZE, 0)}
        generateContent={writeTranscript}
        onBack={onBack}
        mainPlaceholder={window.i18next.t('pmwjs_text_to_speech_ai_writer_placeholder')}
        variant={AiPromptBoxVariant.MONOCONTENT}
        optionsToUse={[PromptBoxConfigOptionType.LENGTH, PromptBoxConfigOptionType.TONE]}
        onClose={isMobile ? onClose : undefined}
        onPromptChange={noop}
        alterationLoadingTexts={alterationLoadingText}
        useContent={useContent}
        reuseContent={reuseContent}
        alterContent={alterContent}
      />
    </div>
  );
}
